<template>
  <div>
    <HeaderBreadcrumbs :items="[
      { label: 'SmartPin', to: { name: 'smartpininsight' } },
      { label: 'Insight' },
    ]" title="Insight" />

    <div class="w-full border-gray-100 rounded-xl mb-5 p-0">
      <div class="w-full bg-white p-4 shadow-sm rounded-xl">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-bold text-gray-900 tracking-tight">
            Summary Onsite Reader
          </h2>
          <DateRangePicker v-model="selectedDateRange" :max-days="31" @change="handleDateRangeChange" />
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="(card, index) in summaryCards" :key="index"
            class="bg-white border h-36 border-gray-200 rounded-2xl p-4 flex flex-col justify-between hover:border-gray-300 transition-colors">
            <div class="w-8 h-8 rounded-lg flex items-start justify-center mb-2" :class="card.iconBg">
              <img v-if="card.type === 'location'" src="../../assets/img/pin.png" class="" alt="" />
              <img v-else-if="card.type === 'scan'" src="../../assets/img/qrcode.png" class="" alt="" />
              <img v-else-if="card.type === 'purchase'" src="../../assets/img/purchase.png" class="" alt="" />
              <img v-else-if="card.type === 'subscribe'" src="../../assets/img/subcribe.png" class="" alt="" />
            </div>

            <div>
              <p class="text-base text-gray-500 font-medium mb-1"
                :class="{ 'underline text-blue-500 cursor-pointer': card.isLink }">
                {{ card.title }}
              </p>
              <h3 class="text-2xl font-bold text-gray-900 leading-tight">
                {{ card.value }}
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex shadow-sm bg-white rounded-xl mt-3 h-auto p-4 w-full items-start justify-start flex-col space-y-8">
        <SummaryInsightCharts 
        :is-loading-funnel="isLoadingFunnel"
        :is-loading-summary="isLoadingSummary"
        :awareness-metrics="awarenessMetrics" 
        :awareness-series="awarenessSeries"
        :awareness-chart-options="awarenessChartOptions" 
        :funnel-series="funnelSeries"
        :funnel-chart-options="funnelChartOptions" 
        :raw-acquisition-values="rawAcquisitionValues" />
      </div>
      <div class="grid p-0 rounded-xl pb-3 grid-cols-1 mt-3 lg:grid-cols-2 gap-6 w-full">
        <div class="flex w-full flex-col bg-white rounded-xl p-4 shadow-sm">
          <div class="w-full flex items-center justify-between px-0">
            <div class="flex-col">
              <h2 class="text-xl mb-0 font-bold text-gray-900 tracking-tight">
                Top Locations
              </h2>
              <p class="text-xs text-gray-700">{{ formatDateRange(this.selectedDateRange) }}</p>
            </div>
            <div>
              <router-link :to="topLocationDetailRoute"
                class="text-xs font-medium text-gray-900 hover:text-blue-600 hover:no-underline">
                See more <i class="fa-solid fa-angle-right"></i>
              </router-link>
            </div>
          </div>
          <div class="w-full px-0">
            <TopLocationsTable :locations="topLocations" :is-loading="isLoadingTopLocations"
              :selected-date-range="selectedDateRange" />
          </div>
        </div>
        <div class="flex w-full flex-col  bg-white rounded-xl p-4 shadow-sm">
          <div class="w-full flex items-center justify-between px-0">
            <div class="flex-col">
              <h2 class="text-xl mb-0 font-bold text-gray-900 tracking-tight">
                Top Content
              </h2>
              <p class="text-xs text-gray-700">{{ formatDateRange(this.selectedDateRange) }}</p>
            </div>
            <div>
              <router-link :to="topContentDetailRoute"
                class="text-xs font-medium text-gray-900 hover:text-blue-600 hover:no-underline">
                See more <i class="fa-solid fa-angle-right"></i>
              </router-link>
            </div>
          </div>
          <div>
            <TopContentsTable :contents="topContents" :is-loading="isLoadingTopContents"
              :selected-date-range="selectedDateRange" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs.vue';
import SummaryInsightCharts from '@/components/smart-pin/SummaryInsightCharts.vue';
import TopLocationsTable from '@/components/smart-pin/TopLocationsTable.vue';
import TopContentsTable from '@/components/smart-pin/TopContentsTable.vue'; // Import komponen baru
import DateRangePicker from '@/components/form/DateRangePicker.vue';
import { toQueryDate } from '@/components/smart-pin/mixins/insightFilterMixin';
import { mapState, mapActions } from 'vuex';
export default {
  name: 'SmartPinInsight',
  components: {
    HeaderBreadcrumbs,
    SummaryInsightCharts,
    DateRangePicker,
    TopContentsTable,
    TopLocationsTable
  },
  data() {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      summaryCards: [],
      awarenessMetrics: [],
      topLocations: [],
      topContents: [],
      awarenessSeries: [],
      rawAcquisitionValues: [0, 0, 0],
      selectedDateRange: [firstDay, now],
      awarenessChartOptions: {
        chart: {
          type: 'bar',
          stacked: true,
          stackType: '100%',
          toolbar: { show: false },
          sparkline: { enabled: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '40%',
            borderRadius: 12
          }
        },
        colors: ['#1A56DB', '#16BDCA', '#FDBA8C'],
        dataLabels: { enabled: false },
        stroke: {
          width: 4,
          colors: ['#ffffff']
        },
        xaxis: {
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: { show: false },
        grid: { show: false },
        legend: {
          show: true,
          position: 'bottom',
          horizontalAlign: 'center',
          fontSize: '12px',
          markers: { radius: 12 },
          itemMargin: { horizontal: 10 }
        },
        tooltip: { enabled: true }
      },
      funnelSeries: [],
      funnelChartOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          sparkline: { enabled: false }
        },
        plotOptions: {
          bar: {
            isFunnel: true,
            horizontal: true,
            barHeight: '70%',
            distributed: true,
            borderRadius: 10,
            borderRadiusApplication: 'around'
          }
        },
        colors: ['#5393F8', '#25C8D4', '#2CD297'],
        dataLabels: {
          enabled: true,
          formatter: (val, opt) => {
            const labels = ['Total Visitor', 'Total Registration', 'Total Reader'];
            const realVal = this.rawAcquisitionValues?.[opt.dataPointIndex] ?? val;
            return `${labels[opt.dataPointIndex]} : ${realVal}`;
          },
          dropShadow: { enabled: false },
          style: {
            fontSize: '13px',
            fontWeight: '600',
            fontFamily: 'Inter, sans-serif',
            colors: ['#ffffff']
          }
        },
        xaxis: {
          categories: ['Total Visitor', 'Total Registration', 'Total Reader'],
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: { show: false },
        grid: {
          show: true,
          borderColor: '#f1f1f1',
          strokeDashArray: 0,
          yaxis: { lines: { show: true } },
          xaxis: { lines: { show: false } }
        },
        legend: { show: false },
        tooltip: {
          enabled: true,
          y: {
            formatter: (val, opt) => {
              return this.rawAcquisitionValues?.[opt.dataPointIndex] ?? val;
            }
          }
        }
      }
    };
  },
  computed: {
    ...mapState('smartpin', ['isLoadingSummary', 'isLoadingFunnel', 'isLoadingTopLocations', 'isLoadingTopContents']),
    topContentDetailRoute() {
      return this.buildDetailRoute('smartpininsightcontent');
    },
    topLocationDetailRoute() {
      return this.buildDetailRoute('smartpininsightlocation');
    }
  },
  mounted() {
    this.initDefaultDateAndFetch();
  },
  roundBars(chartContext) {
    this.$nextTick(() => {
      const bars = chartContext.el.querySelectorAll(
        '.apexcharts-bar-area'
      )

      if (!bars.length) return

      const seriesCount = this.series.length
      const categoryCount = this.series[0].data.length

      for (let i = 0; i < categoryCount; i++) {
        const firstBar = bars[i]
        const lastBar =
          bars[(seriesCount - 1) * categoryCount + i]

        if (firstBar) {
          firstBar.setAttribute('rx', '8')
          firstBar.setAttribute('ry', '8')
        }

        if (lastBar) {
          lastBar.setAttribute('rx', '8')
          lastBar.setAttribute('ry', '8')
        }
      }
    })
  },

  methods: {
    ...mapActions('smartpin', ['getSmartpinSummary', 'getSmartpinFunnel', 'getTopLocations', 'getTopContents']),

    buildDetailRoute(name) {
      const [startDate, endDate] = this.selectedDateRange;
      return {
        name,
        query: { start_date: toQueryDate(startDate), end_date: toQueryDate(endDate) },
      };
    },

    initDefaultDateAndFetch() {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      this.selectedDateRange = [firstDayOfMonth, today];
      this.fetchAllInsightData(firstDayOfMonth, today);
    },

    handleDateRangeChange({ startDate, endDate }) {
      if (startDate && endDate) {
        this.fetchAllInsightData(startDate, endDate);
      }
    },
    formatDateRange(dates) {
      const formatDate = (dateString) => {
        const date = new Date(dateString)

        return new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Jakarta',
          day: 'numeric',
          month: 'short',
          year: '2-digit'
        }).format(date)
      }

      return `This Month (${formatDate(dates[0])} - ${formatDate(dates[1])})`
    },
    async fetchAllInsightData(start = this.selectedDateRange[0], end = this.selectedDateRange[1]) {
      const payload = { startDate: start, endDate: end };

      try {
        const [summaryRes, funnelRes, topLocationsRes, topContentsRes] = await Promise.all([
          this.getSmartpinSummary(payload),
          this.getSmartpinFunnel(payload),
          this.getTopLocations(payload),
          this.getTopContents(payload)
        ]);

        if (summaryRes) this.mapSummaryResponseToCards(summaryRes);
        if (funnelRes) this.mapFunnelResponseToCharts(funnelRes);

        if (topLocationsRes) {
          this.topLocations = topLocationsRes ?? [];
        }

        if (topContentsRes) {
          this.topContents = topContentsRes ?? [];
        }
      } catch (error) {
        console.error('Error fetching SmartPin Insight Data:', error);
      }
    },
    mapSummaryResponseToCards(data) {
      this.summaryCards = [
        {
          title: 'All Locations',
          value: data.all_locations ?? 0,
          type: 'location',
          iconBg: 'bg-teal-50',
          isLink: false
        },
        {
          title: 'Total Scan',
          value: data.total_scan ?? 0,
          type: 'scan',
          iconBg: 'bg-blue-50',
          isLink: false
        },
        {
          title: 'Purchase After Scan',
          value: `Rp${(data.purchase_single.amount ?? 0).toLocaleString('id-ID')}`,
          type: 'purchase',
          iconBg: 'bg-purple-50',
          isLink: false
        },
        {
          title: 'Subscribe After Scan',
          value: `Rp${(data.purchase_buffets.amount ?? 0).toLocaleString('id-ID')}`,
          type: 'subscribe',
          iconBg: 'bg-amber-50',
          isLink: false
        }
      ];
    },

    mapFunnelResponseToCharts(data) {
      const awareness = data.awareness || {};
      const acquisition = data.acquisition || {};
      const installVal = Number(awareness.camera_to_install ?? 0);
      const appsVal = Number(awareness.camera_to_apps ?? 0);
      const onsiteVal = Number(awareness.camera_to_onsite ?? 0);

      this.awarenessMetrics = [
        { label: 'Camera to Install', value: installVal, type: 'install' },
        { label: 'Camera to Apps', value: appsVal, type: 'apps' },
        { label: 'Camera to Onsite', value: onsiteVal, type: 'onsite' }
      ];

      const isAllZero = installVal === 0 && appsVal === 0 && onsiteVal === 0;

      this.awarenessSeries = [
        { name: 'Camera to Install', data: [isAllZero ? 1 : installVal] },
        { name: 'Camera to Apps', data: [isAllZero ? 1 : appsVal] },
        { name: 'Camera to Onsite', data: [isAllZero ? 1 : onsiteVal] }
      ];

      const totalVisitor = Number(acquisition.total_visitor ?? 0);
      const totalRegistration = Number(acquisition.total_registration ?? 0);
      const totalReader = Number(acquisition.total_reader ?? 0);
      this.rawAcquisitionValues = [totalVisitor, totalRegistration, totalReader];

      const maxVal = Math.max(totalVisitor, totalRegistration, totalReader, 1);
      const BASE_MIN = 20; 
      const RANGE = 100 - BASE_MIN; 
      const calculateDynamicWidth = (val) => {
        if (val === 0) return BASE_MIN;
        const ratio = val / maxVal;
        return BASE_MIN + (ratio * RANGE);
      };
      const visualVisitor = calculateDynamicWidth(totalVisitor);
      const visualRegistration = calculateDynamicWidth(totalRegistration);
      const visualReader = calculateDynamicWidth(totalReader);

      this.funnelSeries = [
        {
          name: 'Acquisition',
          data: [visualVisitor, visualRegistration, visualReader]
        }
      ];
    },
  }
}
</script>
